import {
  failedCommentaire,
  failedUrlAnnonce,
  loadingCommentaire,
  loadingUrlAnnonce,
  succeededCommentaire,
  succeededUrlAnnonce,
  toggleModalUrlAnnonce as toggleModalUrlAnnonceAction,
  toggleModalCommentaire as toggleModalCommentaireAction,
  idleUrlAnnonce as idleUrlAnnonceAction,
  idleCommentaire as idleCommentaireAction,
  Status,
} from 'redux/features/favoris/slice'
import { makeSelectFavoris } from 'redux/features/favoris/selectors'

import { useAppDispatch, useAppSelector } from 'redux/hooks'
import { fetchWithCheckAuthorization } from 'utils/fetchWithAuthorization'

interface ReturnProps {
  commentaire: string
  urlAnnonce: string
  statusCommentaire: Status
  errorCommentaire: string | null
  statusUrlAnnonce: Status
  errorUrlAnnonce: string | null
  showModalUrlAnnonce: boolean
  showModalCommentaire: boolean
  toggleModalUrlAnnonce: (show?: boolean) => void
  toggleModalCommentaire: (show?: boolean) => void
  updateUrlAnnonce: (urlAnnonce: string | null) => void
  updateCommentaire: (commentaire: string | null) => void
  idleUrlAnnonce: () => void
  idleCommentaire: () => void
}

interface PatchBody {
  uidParcelle: string
  urlAnnonce?: string | null
  commentaire?: string | null
}

const useUrlAnnonceCommentaire = (uid: string): ReturnProps => {
  const favoris = useAppSelector(makeSelectFavoris)
  const favori = favoris[uid]
  const dispatch = useAppDispatch()

  const patchFavori = async ({
    urlAnnonce,
    commentaire,
  }: {
    urlAnnonce?: string | null
    commentaire?: string | null
  }) => {
    let body: PatchBody = {
      uidParcelle: uid,
    }

    if (urlAnnonce !== undefined) {
      body = {
        ...body,
        urlAnnonce,
      }
    }
    if (commentaire !== undefined) {
      body = {
        ...body,
        commentaire,
      }
    }
    const response = await fetchWithCheckAuthorization(`/api/favori/${uid}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
    if (response.ok) return
    const json = await response.json()
    switch (response.status) {
      case 400:
        throw Error(json.message[0])
      case 500:
        throw Error(json.detail)
      default:
        throw Error('Une erreur est survenue')
    }
  }

  const updateUrlAnnonce = async (urlAnnonce: string | null) => {
    try {
      dispatch(loadingUrlAnnonce(uid))
      await patchFavori({ urlAnnonce })
      dispatch(succeededUrlAnnonce({ uid, urlAnnonce }))
    } catch (err: unknown) {
      console.error(err)
      let errMsg = (err as Error).toString()
      if (errMsg.includes('urlAnnonce must be a valid URL.'))
        errMsg = "L'url n'est pas valide."
      dispatch(failedUrlAnnonce({ uid, errMsg }))
    }
  }

  const updateCommentaire = async (commentaire: string | null) => {
    try {
      dispatch(loadingCommentaire(uid))
      await patchFavori({ commentaire })
      dispatch(succeededCommentaire({ uid, commentaire }))
    } catch (err: unknown) {
      console.error(err)
      dispatch(failedCommentaire({ uid, error: (err as Error).toString() }))
    }
  }

  const toggleModalUrlAnnonce = (show?: boolean) => {
    dispatch(toggleModalUrlAnnonceAction({ uid, show }))
  }

  const toggleModalCommentaire = (show?: boolean) => {
    dispatch(toggleModalCommentaireAction({ uid, show }))
  }

  const idleUrlAnnonce = () => {
    dispatch(idleUrlAnnonceAction(uid))
  }

  const idleCommentaire = () => {
    dispatch(idleCommentaireAction(uid))
  }

  return {
    commentaire: favori.commentaire,
    urlAnnonce: favori.urlAnnonce,
    statusCommentaire: favori.statusCommentaire,
    errorCommentaire: favori.errorCommentaire,
    statusUrlAnnonce: favori.statusUrlAnnonce,
    errorUrlAnnonce: favori.errorUrlAnnonce,
    showModalUrlAnnonce: favori.showModalUrlAnnonce,
    showModalCommentaire: favori.showModalCommentaire,
    toggleModalUrlAnnonce,
    toggleModalCommentaire,
    updateUrlAnnonce,
    updateCommentaire,
    idleUrlAnnonce,
    idleCommentaire,
  }
}

export default useUrlAnnonceCommentaire
